import { useSelector } from 'react-redux';
import { useCallback } from 'react';
import { ApplicationState } from '@mews-commander/types';
import { LocalizedText, useAppNowUtc, useFeatureFlag } from '@mews/framework';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { Banner, Stack } from '@optimus-web/core';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { LocalizedDateFromUtc, LocalizedShortTimeFromUtc } from '@mews-commander/ui';
import { selectReservationsLatestUndoCancelationUtc } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useInvalidateReservationGroupQuery } from '@mews-commander/core/entities';
import { getCheckedReservationIds } from '../../selectors';
import { selectionIsCanceled } from '../../selectors/reservationModuleState/reservationSelection';
import { formCardFactory } from '../formCardFactory';
import { fetchCurrentEnterpriseCheckedReservations, fetchReservationModuleReduxEntities } from '../../actions';
import { UndoReservationCancellationForm } from './UndoReservationCancellationForm';

interface ElementProps {
    form: string;
    reservationIds: string[];
    refetchGroup: () => void;
}
interface FormCardProps {
    form: string;
    scope: string;
}

const FormElement = (props: ElementProps & { onSuccess: () => void }) => {
    const { reservationIds } = props;
    const nowUtc = useAppNowUtc();
    const enterpriseId = useCurrentEnterpriseId();
    const latestUndoCancellationUtc = useCommanderSelector((state) =>
        selectReservationsLatestUndoCancelationUtc(state, { ids: reservationIds, enterpriseId })
    );

    if (!latestUndoCancellationUtc) return null;

    if (nowUtc.value < latestUndoCancellationUtc) {
        return (
            <Stack vertical spacing="200">
                <Banner
                    variant="info"
                    primaryText={
                        <LocalizedText
                            textKey={TextKey.ReservationsCancellationAvailable}
                            substitutes={{
                                date: <LocalizedDateFromUtc utcString={latestUndoCancellationUtc} />,
                                time: <LocalizedShortTimeFromUtc utcString={latestUndoCancellationUtc} />,
                            }}
                        />
                    }
                />
                <UndoReservationCancellationForm
                    {...props}
                    showConfirmationDialog
                    confirmationDialogTitle={TextKey.ReservationUndoCancellationDialogTitle}
                    confirmationDialogContent={
                        <LocalizedText textKey={TextKey.ReservationUndoCancellationDialogContent} />
                    }
                    confirmationDialogConfirmButtonTextKey={TextKey.RestoreAnyway}
                    confirmationDialogConfirmButtonVariant="primary"
                />
            </Stack>
        );
    }
    return (
        <Banner
            variant="info"
            primaryText={<LocalizedText textKey={TextKey.ReservationsCancelledPernamently} />}
            secondaryText={<LocalizedText textKey={TextKey.ReservationsCancelledPernamentlyHint} />}
        />
    );
};
const UndoReservationCancellationFormCardElement = formCardFactory<ElementProps>({
    FormElement,
    titleTextKey: TextKey.RecoverCanceledReservations,
    onSuccessMessageKey: TextKey.ReservationUndoCancellationSuccessMessage,
});

const UndoCancellationFormCard = ({ form, scope }: FormCardProps) => {
    const invalidateReservations = useInvalidateReservationGroupQuery(scope);
    const invalidateOrderItems = useInvalidateReservationGroupQuery(scope, { invalidateOrderItemsQuery: true });
    const invalidateReservationData = useCallback(async () => {
        await invalidateReservations();
        // We need to wait until the query switches to load canceled reservations
        await invalidateOrderItems();
    }, [invalidateReservations, invalidateOrderItems]);
    const isReservationModuleReactQueryEnabled = useFeatureFlag(FeatureFlag.reservationModuleReactQueryEnabled);

    const dispatch = useCommanderThunkDispatch();
    const refetchGroup = async () =>
        Promise.all([
            dispatch(fetchReservationModuleReduxEntities(scope, { balances: true })),
            isReservationModuleReactQueryEnabled
                ? invalidateReservationData()
                : dispatch(fetchCurrentEnterpriseCheckedReservations({ scope })),
        ]);

    const isCanceled = useSelector((state: ApplicationState) => selectionIsCanceled(state, { scope }));
    const reservationIds = useSelector((state: ApplicationState) => getCheckedReservationIds(state, { scope }));

    if (!isCanceled) return null;

    return (
        <UndoReservationCancellationFormCardElement
            form={form}
            reservationIds={reservationIds}
            refetchGroup={refetchGroup}
        />
    );
};
export default UndoCancellationFormCard;
